import React, {GoogleMap, LoadScript, Marker} from "@react-google-maps/api"
import {useContext, useState} from "react"
import {useTranslation} from "react-i18next"
import axios from "axios"
import Typography from "../../reusable/Typography/Typography"
import Button from "../../reusable/components/Button" // Import axios to fetch geolocation data
import LayoutContext from "../../../context/LayoutContext"

const GoogleMapMBP = ({mapApiKey, locations, isPdp = false}: { mapApiKey: string; locations: any[], isPdp?:boolean }) => {
	const {t} = useTranslation()
	const [selectedZip, setSelectedZip] = useState("")
	const [filteredLocations, setFilteredLocations] = useState(locations)
	const [center, setCenter] = useState({lat: locations[0]?.lat, lng: locations[0]?.lng}) // Default center
	const {isMobile} = useContext(LayoutContext)
	const containerStyleMobile = {width: "100%", height: "280px"}
	const containerStyleDesktop = {width: "100%", height: "550px"}

	const haversineDistance = (lat1: number, lng1: number, lat2: number, lng2: number) => {
		const toRad = (value: number) => (value * Math.PI) / 180
		const Radius = 6371 // Earth's radius in km

		const dLat = toRad(lat2 - lat1)
		const dLng = toRad(lng2 - lng1)

		const a =

			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *

			Math.sin(dLng / 2) * Math.sin(dLng / 2)

		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
		return Radius * c // Distance in km
	}

	const handleSearch = async () => {
		try {
			const response = await axios.get(
				`https://maps.googleapis.com/maps/api/geocode/json?address=${selectedZip}&components=country:CH&key=${mapApiKey}`
			)

			const location = response.data.results[0]?.geometry.location
			if (location) {
				// Calculate distances for all locations
				const locationsWithDistance = locations.map(loc => ({
					...loc,
					distance: haversineDistance(location.lat, location.lng, loc.lat, loc.lng)
				}))

				// Sort locations by distance (nearest first)
				const sortedLocations = locationsWithDistance.sort((a, b) => a.distance - b.distance)

				// Get the nearest location
				const nearestLocation = sortedLocations[0]

				// Update state
				setFilteredLocations(sortedLocations.slice(0, 5)) // Show top 5 closest locations

				// Center map on nearest location if available
				if (nearestLocation) {
					setCenter({lat: nearestLocation.lat, lng: nearestLocation.lng})
				} else {
					setCenter({lat: location.lat, lng: location.lng}) // Fallback to geocoded ZIP location
				}
			}
		} catch (error) {
			console.error("Geocoding API error:", error)
		}
	}

	return (
		<div className={isPdp ? "" : "my-5"}>
			<div className={isPdp ? "d-flex col-12 align-items-start justify-content-start flex-column" : "row align-items-center"}>
				<div className={isPdp ? "col-12" : "col-12 col-md-6"}>
					<div className={isPdp ? "" : "mb-4 mt-5"}>
						{isPdp ? null : <Typography variant={"heading2"}
							semanticTag={"h2"}>
							{t("service-center-locations-title")}
						</Typography>}

						<Typography variant={"bodyLg"}
							semanticTag={"p"}
							className={"mt-2"}>
							{t("service-center-locations-text")}
						</Typography>
					</div>

					{isPdp ? null : <div className="d-md-none w-100 h-auto h-md-100">
						<LoadScript googleMapsApiKey={mapApiKey}>
							<GoogleMap mapContainerStyle={containerStyleMobile}
								center={center}
								zoom={16}>
								{filteredLocations.map(loc => (
									<Marker key={loc.id}
										position={{lat: loc.lat, lng: loc.lng}} />
								))}
							</GoogleMap>
						</LoadScript>
					</div>}

					<div className="row justify-content-start my-4 text-left">
						<div className="col-12">
							<label htmlFor={"zip"}
								className={"text-left mb-2"}>
								<Typography variant={"bodySmBold"}
									semanticTag="span">
									{t("service-center-zip-label")}
								</Typography>
								<span className="text-danger">*</span>
							</label>
							{isPdp ? <div style={{height: "40px"}}
								className="w-md-auto mb-3 border ps-3 rounded-3 w-100 d-flex justify-content-start align-items-center">

								<input
									style={{border: "none", flex: 1, outline: "none"}}
									type="text"
									className="  text-capitalize   "
									value={selectedZip}
									onChange={e => {
										const numericValue = e.target.value.replace(/\D/g, "") // Remove non-numeric characters
										setSelectedZip(numericValue)
									}}
								/><Button
									className={" border-left-only  w-100 w-auto"}
									btnFontSize={14}
									borderColor="#DEE2E1"
									color="#000"
									outline
									fontWeight={600}
									onClick={handleSearch}>
									{t("find-zip-service-center")}
								</Button>

							</div> : <input
								type="text"
								className="form-control w-100 w-md-auto mb-3 py-2 px-3 rounded-3 text-capitalize border service-search-field"
								value={selectedZip}
								onChange={e => {
									const numericValue = e.target.value.replace(/\D/g, "") // Remove non-numeric characters
									setSelectedZip(numericValue)
								}}
							/>}
						</div>
						{isPdp ? null : <div className="col-12 w-100">
							<Button className={"d-md-none btn-primary w-100 w-md-auto"}
								btnFontSize={14}
								fontWeight={600}
								onClick={handleSearch}>
								{t("find-zip-service-center")}
							</Button>
							<Button className={"d-none d-md-block btn-primary w-100 w-auto"}
								btnFontSize={14}
								fontWeight={600}
								onClick={handleSearch}>
								{t("find-zip-service-center")}
							</Button>
						</div>}
					</div>
				</div>
				<div className={isPdp ? "col-12" : "col-12 col-md-6"}>
					<div className={isPdp ? "d-md-block w-100 h-100" : "d-none d-md-block w-100 h-100"}>
						<LoadScript googleMapsApiKey={mapApiKey}>
							<GoogleMap mapContainerStyle={isPdp ? {...containerStyleMobile, borderRadius: "4px"} : containerStyleDesktop}
								center={center}
								zoom={16}>
								{filteredLocations.map(loc => (
									<Marker
										key={loc.id}
										position={{lat: loc.lat, lng: loc.lng}}
										label={{text: loc.name, color: "black", fontSize: "14px"}}
									/>
								))}
							</GoogleMap>
						</LoadScript>
					</div>
				</div>
			</div>
		</div>
	)
}

export default GoogleMapMBP

